import React, { useState } from 'react';

import introImg from '../../../../assets/images/courses/team-players/chapter2.1.jpg';
import chapterImg1 from '../../../../assets/images/courses/team-players/chapter2.2.jpg';
import chapterImg2 from '../../../../assets/images/courses/team-players/chapter2.3.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { RatingTable, TopFiveResults } from '../../../Course/RoleCategory';
import { JigSawModule } from '../../JigSawModule';
import { MediaBlock } from '../../MediaBlock';
import { TabsBlock } from '../../TabsBlock';

interface TeamPlayersProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersChapter2: React.FC<TeamPlayersProps> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const [ratings, setRatings] = useState<{ [key: string]: number } | null>(null);

  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const boxes = [
    {
      id: '1',
      title: 'Management Roles',
      content: (
        <div>
          <p>Within a team, three key management roles must be filled:</p>
          <ul className="pl-5 mb-5 font-light">
            <li>
              <strong> a.</strong> Organizer:This individual manages people and ideas, ensuring
              everything is orderly and structured.
            </li>
            <li>
              <strong> b.</strong> Driver: The driver is focused on speed and achieving results,
              keeping a close eye on deadlines and time management. This role may differ from that
              of the organizer.
            </li>
            <li>
              <strong>c.</strong> Visionary: This person has a strategic, long-term perspective,
              thinking ahead and guiding the team toward future goals.
            </li>
          </ul>
          <p>
            These management roles can be fulfilled by one exceptional leader or by two or three
            different team members. Consider which roles your boss handles and which ones come
            naturally to you.
          </p>
        </div>
      ),
      color: '#c00000', //red
    },
    {
      id: '2',
      title: 'People Roles',
      content: (
        <div>
          <p>Within a team, there are three people roles to consider:</p>
          <ul className="pl-5 mb-5 font-light">
            <li>
              <strong> a.</strong> Networker: This individual thrives on meeting new people and
              gathering fresh ideas. They're often outgoing and talkative, though not always the
              most organized. Their strength lies in exploring new concepts and connections.
            </li>
            <li>
              <strong> b.</strong> Carer: The carer is perceptive and empathetic, often a quiet
              presence in the team. They excel at recognizing the value in others' ideas and
              ensuring everyone feels heard and supported. They might check in on quieter team
              members to ensure they're doing okay.
            </li>
            <li>
              <strong>c.</strong> Inspirer: This person ignites motivation, enthusiasm, and
              sometimes even brings a sense of fun to the team. They play a vital role in keeping
              morale high and ensuring the team stays engaged and excited about their work. Imagine
              a team without their uplifting influence.
            </li>
          </ul>
          <p>
            These roles complement the management roles previously mentioned and contribute to the
            overall success and well-being of the team.
          </p>
        </div>
      ),

      color: '#54a503', //green
    },
    {
      id: '3',
      title: 'Creativity & Problem Solving',
      content: (
        <div>
          <p>
            Within the realm of generating ideas and making decisions, there are three crucial
            roles:
          </p>
          <ul className="pl-5 mb-5 font-light">
            <li>
              <strong> a.</strong> Creative type: This individual excels at generating entirely new
              ideas from their imagination. Their ability to think outside the box is admirable and
              often serves as a catalyst for innovation.
            </li>
            <li>
              <strong> b.</strong> Opposer: The opposer plays the role of the devil's advocate,
              questioning the validity of ideas and pointing out potential flaws in the plan. While
              not always the most popular voice, they provide a critical perspective that is
              immensely valuable in refining and strengthening ideas.
            </li>
            <li>
              <strong>c.</strong> Judge: The judge's responsibility is to evaluate the various
              options presented, select the most suitable one, and ensure the team focuses on honing
              a concrete plan rather than endlessly brainstorming. They play a pivotal role in
              guiding the team towards making informed decisions and taking decisive action.
            </li>
          </ul>
          <p>
            These roles complement each other in the creative and decision-making process,
            contributing to the team's ability to generate innovative ideas and execute effective
            strategies.
          </p>
        </div>
      ),
      color: '#ffc000', //yellow
    },
    {
      id: '4',
      title: 'Work Roles',
      content: (
        <div>
          <p>
            In addition to the previously mentioned roles, there are three more roles vital to the
            team dynamic:
          </p>
          <ul className="pl-5 mb-5 font-light">
            <li>
              <strong> a.</strong> Detail person: This individual ensures that every aspect of the
              work is precisely accurate and in line with standards. Their meticulous attention to
              detail is crucial for maintaining quality and consistency in the team's output.
            </li>
            <li>
              <strong> b.</strong> Systems thinker: The systems thinker takes a step back to analyze
              and organize the processes and workflows within the team. They design systems and
              structures to ensure efficiency and smooth operation, contributing to the overall
              effectiveness of the team's efforts.
            </li>
            <li>
              <strong>c.</strong> Stayer: The stayer is adept at handling routine and repetitive
              tasks with patience and persistence. While they may not receive much recognition,
              their commitment and endurance are invaluable for completing essential but less
              glamorous work. They play a crucial role in the team's productivity and success.
            </li>
          </ul>
          <p>
            Since your team might not be as large as 12 people, each team member will likely take on
            multiple roles from the list provided. Look at the roles and consider which ones you
            excel at.
          </p>
        </div>
      ),
      color: '#1f497d', //blue
    },
  ];
  const showYoureListeningTabData = [
    {
      title: 'MANAGEMENT ROLES',

      htmlContent: `
  <ul className=" mb-14 text-lg leading-10 font-light list-disc ">
    <li className="mb-7">
      <strong>Organizer (Alex):</strong> Alex ensures that all tasks are clearly outlined and assigned, creates a detailed timeline, and keeps everyone on track.
    </li>
      <br />
    <li className="mb-7">
      <strong>Driver (Taylor):</strong> Taylor focuses on meeting deadlines, pushing the team to stay on schedule, and ensuring tasks are completed efficiently.
    </li>
      <br />
    <li className="mb-7">
      <strong>Visionary (Morgan):</strong> Morgan comes up with the overall concept for the fundraiser, envisioning a community fair that will attract a lot of attendees and raise significant funds.
    </li>
  </ul>

`,
    },
    {
      title: 'PEOPLE ROLES',

      htmlContent: `<ul className="pl-5 mb-14 text-xl font-light">
  <li className="mb-7">
    <strong>Networker (Jamie):</strong> Jamie reaches out to local businesses for sponsorships and donations, bringing in new ideas and connections to support the event.
  </li>
  <br />
  <li className="mb-7">
    <strong>Carer (Sam):</strong> Sam checks in with each team member regularly, ensuring everyone feels supported and that their opinions are valued, maintaining a positive team atmosphere.
  </li>
    <br />
  <li className="mb-7">
    <strong>Inspirer (Jordan):</strong> Jordan keeps team morale high with enthusiasm and motivational talks, making the work environment enjoyable and encouraging everyone to stay engaged.
  </li>
</ul>
`,
    },
    {
      title: 'CREATIVITY & PROBLEM SOLVING ROLES',

      htmlContent: `<ul className="pl-5 mb-14 text-xl font-light">
    <li className="mb-7">
      <strong>Creative type (Chris):</strong>  Chris designs eye-catching posters and promotional materials, and comes up with innovative ideas for games and activities at the fair.
    </li>
    <br />
    <li className="mb-7">
      <strong>Opposer (Avery): </strong> Avery reviews the plans critically, identifying potential issues and flaws, ensuring that all ideas are feasible and well-thought-out.
    </li>
      <br />
    <li className="mb-7">
      <strong>Judge (Taylor):</strong> Taylor evaluates the different ideas presented by Chris and others, deciding on the best ones to implement and ensuring the team focuses on a clear plan.
    </li>
  </ul>
  `,
    },
    {
      title: 'WROKING ROLES',

      htmlContent: `<ul className="pl-5 mb-14 text-xl font-light">
      <li className="mb-7">
        <strong>Detail person (Alex):</strong>  Alex double-checks all the logistical details, such as booking the venue, arranging equipment, and ensuring compliance with safety regulations.
      </li>
      <br />
      <li className="mb-7">
        <strong>Systems thinker (Morgan): </strong> Systems thinker (Morgan): Morgan organizes the workflow, creating efficient systems for ticket sales, volunteer coordination, and on-the-day event management.
        <br />
         <br />
      <li className="mb-7">
        <strong>Stayer (Sam):</strong> Sam takes on routine tasks like managing the ticket booth and coordinating volunteers on the day of the event, ensuring everything runs smoothly.
      </li>
    </ul>
    `,
    },
  ];

  const roleCategories = [
    {
      title: 'Management roles',
      roles: [
        { name: 'Organizer :', description: 'Organizes people and ideas' },
        { name: 'Driver :', description: 'Pushes for speed and results' },
        { name: 'Visionary :', description: 'Has a long-term vision and strategy' },
      ],
    },
    {
      title: 'People roles',
      roles: [
        { name: 'Networker :', description: 'Meets new people and collects ideas' },
        { name: 'Inspirer :', description: 'Inspires and motivates people' },
        { name: 'Carer :', description: 'Notices and supports people if unhappy' },
      ],
    },
    {
      title: 'Creativity & Problem Solving',
      roles: [
        { name: 'Creative thinker :', description: 'Generates new ideas' },
        { name: 'Opposer :', description: 'Finds flaws and speaks up' },
        { name: 'Judge :', description: 'Assesses and selects the best option' },
      ],
    },
    {
      title: 'Work roles',
      roles: [
        { name: 'Detail person :', description: 'Checks quality and finishes work' },
        { name: 'Systems manager :', description: 'Sets up efficient processes' },
        { name: 'Stayer :', description: 'Has patience for routine work' },
      ],
    },
  ];

  const columns = ['Poor', 'Below Average', 'Good', 'Very Good', 'Outstanding'];
  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Team Roles</h2>

        <MediaBlock mediaImage={introImg}></MediaBlock>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          In a team, everyone has specific roles and responsibilities to ensure that the group works
          effectively and reaches its goals.
        </p>
        <br />

        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1 leading-9">
            <br />
            Each team role is crucial for the success of the team, and often, team members might
            take on multiple roles depending on the situation. Working together effectively by
            understanding and respecting each other's responsibilities helps the team achieve its
            goals efficiently and enjoyably.
          </p>
        </div>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-1">Division of Work</h3>

        <p className="text-lg text-left mt-7 mb-14 leading-10">
          In a successful team, there are four main areas of responsibility:
        </p>
        <ul className="list-decimal pl-5 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">
            First, there's management, where someone organizes and plans for the team's long-term
            goals.
          </li>
          <li className="pl-9 mb-7">
            Second, there's taking care of the people in the team, making sure everyone feels
            supported and valued.
          </li>
          <li className="pl-9 mb-7">
            Third, there's fostering creativity, especially when the team needs to come up with new
            ideas or solutions.
          </li>
          <li className="pl-9 mb-7">
            Finally, there's the actual execution of the work – someone has to get it done. These
            four areas are essential for a team to thrive.
          </li>
        </ul>

        <p className="text-xl font-bold text-left mt-7 mb-20 leading-10">
          Within each of the four areas of responsibility, there are three main roles that have been
          identified.
        </p>

        <p className="text-lg font-bold text-left mt-7 mb-20 leading-10">
          Click each puzzle piece to learn more.
        </p>
        {/* pending */}

        <JigSawModule backgroundColor="#f3f4f6" boxes={boxes} />

        <br />

        <h3 className="text-3xl mt-7 mb-20  leading-10 font-bold mb-1">
          Case Study Example: "The School Fundraiser Project"
        </h3>
        <p className="text-xl font-bold text-left mt-7 mb-10 ">Overview:</p>
        <p className="text-xl font-light mb-8">
          A group of students is tasked with organizing a school fundraiser to raise money for new
          library books. The success of the project depends on effective collaboration among the
          team members, each taking on specific roles to ensure all aspects are covered.
        </p>
        <p className="text-xl font-bold text-left mt-7 mb-10 ">Roles and Responsibilities:</p>
        <p className="text-xl font-light mb-8">
          Click each tab to learn more about each person and their role and responsibility.
        </p>

        <TabsBlock tabsData={showYoureListeningTabData} />

        <h3 className="text-2xl text-left font-bold mb-1">Scenario:</h3>
        <div className="flex flex-col items-center relative min-h-screen py-10">
          {/* Vertical Line (Stops before the last div) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-[calc(100%-100px)]"></div>

          {/* Cards */}
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Visionary, Creative, Networker
            </h3>
            <h3 className="text-xl text-left font-semibold mb-5">Morgan, Chris, Jamie</h3>
            <p className="text-xl font-light mb-8">
              The school fundraiser project kicks off with a brainstorming session led by Morgan,
              the Visionary, who proposes a community fair as the main event. Chris, the Creative
              type, starts developing ideas for games and activities, while Jamie, the Networker,
              contacts local businesses for sponsorships and donations.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Organizer & Driver</h3>
            <h3 className="text-xl text-left font-semibold mb-5">Alex & Taylor</h3>
            <p className="text-xl font-light mb-8">
              As plans begin to take shape, Alex, the Organizer, assigns tasks to team members and
              creates a detailed timeline. Taylor, the Driver, pushes everyone to meet deadlines,
              ensuring that promotional materials are ready and distributed on time.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Opposer & Judge</h3>
            <h3 className="text-xl text-left font-semibold mb-5">Avery & Taylor</h3>
            <p className="text-xl font-light mb-8">
              Throughout the planning process, Avery, the Opposer, critically evaluates each idea,
              pointing out potential issues and ensuring the feasibility of the plans. Taylor, the
              Judge, makes final decisions on which activities to include, balancing creativity with
              practicality.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Carer & Inspirer</h3>
            <h3 className="text-xl text-left font-semibold mb-5">Sam & Jordan</h3>
            <p className="text-xl font-light mb-8">
              Meanwhile, Sam, the Carer, ensures that all team members feel supported and heard,
              creating a positive and collaborative atmosphere. Jordan, the Inspirer, keeps the team
              motivated and enthusiastic, organizing small team-building activities and celebrations
              to maintain high morale.
            </p>
          </div>
        </div>
        <br />
        <h3 className="text-2xl text-left font-bold mb-1">Outcome</h3>

        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-6">
            <br />
            The community fair is a huge success, thanks to the effective collaboration and clearly
            defined roles within the team. The event raises a significant amount of money for the
            school library, demonstrating the power of teamwork and the importance of each role in
            achieving common goals. The team members feel a sense of accomplishment and pride in
            their collective effort, reinforcing the value of their diverse contributions.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold mb-8">
          Activity: What Roles Do You Excel At?
        </h3>
        <p className="text-xl font-light mb-8">
          Complete the assessment to help determine which roles you excel at. Rate your level of
          proficiency as: Poor | Below Average | Good | Very Good | Outstanding. You may print your
          responses upon completion.
        </p>
        {/*  Role category table */}
        <div className="p-6 bg-white rounded-lg shadow-md mb-6 mt-6">
          {ratings ? (
            <TopFiveResults
              ratings={ratings}
              roleCategories={roleCategories}
              onRetake={() => setRatings(null)}
            />
          ) : (
            <RatingTable
              roleCategories={roleCategories}
              onSubmit={setRatings}
              column={columns}
              firstColumn="Role Category"
            />
          )}
        </div>
        <br />

        <h3 className="text-2xl text-purple-600 font-bold mb-1">Team Gaps</h3>
        <p className="text-xl font-light mb-8">
          Think about a team you work with and identify the areas where your team might have gaps.
          If there's a gap, there are two options:
        </p>
        <ul className="list-decimal pl-5 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">
            Someone in the team needs to step up and fill that role, even if it's not their first
            choice, or
          </li>
          <li className="pl-9 mb-7">Recruit or borrow someone to fill that gap naturally.</li>
        </ul>
        <br />
        <h3 className="text-2xl mt-2  text-center font-light mb-5">
          It's essential to identify and address these gaps to ensure your team operates effectively
          and efficiently. Next we will look at the phases of a team task.
        </h3>
        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Phases of a Team Task
        </button>
      </div>
    </>
  );
};
